/* eslint-disable no-useless-catch */

import config from "./config";
import {
  PendingAgreements,
  CompletedAgreements,
  Statements,
  SignedMandates,
  EocConfirmations,
  SwitchConfirmations,
  SwitchConfirmation,
  Statement,
  CompletedAgreement,
  SignedMandate,
  PortalsDocument,
} from "../types/documentTypes";
import { getSessionFromCookie } from "../components/utils/cognito";
import { FetchMeterValuesResponse } from "../types/instalationsTypes";

const session = getSessionFromCookie();
const token = session?.getAccessToken().getJwtToken();

const getPendingAgreements = async (company_id: number): Promise<PendingAgreements> => {
  try {
    console.log("Token:", token);
    const response = await fetch(`${config.BASE_URL}/api/documents/getPendingAgreements/${company_id}`, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (err) {
    throw err;
  }
};

const getCompletedAgreements = async (company_id: number): Promise<CompletedAgreements> => {
  try {
    const response = await fetch(`${config.BASE_URL}/api/documents/getCompletedAgreements/${company_id}`, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (err) {
    throw err;
  }
};

const getStatements = async (company_id: number): Promise<Statements> => {
  try {
    const response = await fetch(`${config.BASE_URL}/api/documents/getStatements/${company_id}`, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (err) {
    throw err;
  }
};

const getStatement = async (company_id: number, document_key: string): Promise<Statement> => {
  try {
    const response = await fetch(
      `${config.BASE_URL}/api/documents/getStatement/${company_id}?document_key=${encodeURIComponent(document_key)}`,
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (err) {
    throw err;
  }
};

const getCompletedAgreement = async (company_id: number, document_key: string): Promise<CompletedAgreement> => {
  try {
    const response = await fetch(
      `${config.BASE_URL}/api/documents/getCompletedAgreement/${company_id}?document_key=${encodeURIComponent(
        document_key,
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching completed agreement:", err);
    throw err;
  }
};

const getSignedMandates = async (company_id: number): Promise<SignedMandates> => {
  try {
    const response = await fetch(`${config.BASE_URL}/api/documents/getSignedMandates/${company_id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching signed mandates:", err);
    throw err;
  }
};

const getSignedMandate = async (company_id: number, document_key: string): Promise<SignedMandate> => {
  try {
    const response = await fetch(
      `${config.BASE_URL}/api/documents/getSignedMandate/${company_id}?document_key=${encodeURIComponent(
        document_key,
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching signed mandate:", err);
    throw err;
  }
};

const getEocConfirmations = async (company_id: number): Promise<EocConfirmations> => {
  try {
    const response = await fetch(`${config.BASE_URL}/api/documents/getEocConfirmations/${company_id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching EOC confirmations:", err);
    throw err;
  }
};

const getSwitchConfirmations = async (company_id: number): Promise<SwitchConfirmations> => {
  try {
    const response = await fetch(`${config.BASE_URL}/api/documents/getSwitchConfirmations/${company_id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching switch confirmations:", err);
    throw err;
  }
};

const getSwitchConfirmation = async (company_id: number, document_key: string): Promise<SwitchConfirmation> => {
  try {
    const response = await fetch(
      `${config.BASE_URL}/api/documents/getSwitchConfirmation/${company_id}?document_key=${encodeURIComponent(
        document_key,
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Error fetching switch confirmation:", err);
    throw err;
  }
};

export const getEanByInstallationId = async (
  installationId: string,
  type: "injection" | "offtake",
  token: string,
): Promise<string | null> => {
  const url = `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/ean/${installationId}/${type}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json();
      throw new Error(errorData.error || "An error occurred while fetching the EAN.");
    }
    const data = await response.json();
    return data.ean;
  } catch (error) {
    console.error("Error fetching EAN:", error);
    throw error;
  }
};

export const fetchMeterValues = async (
  companyId: string,
  token: string,
  type: "injection" | "offtake",
  eans: string[],
  from: string,
  to: string,
  grouped: boolean,
  hourly: boolean,
): Promise<FetchMeterValuesResponse> => {
  const url = `${config.BASE_URL}/api/metervalues/getMetervalues/${type}Hourly/${companyId}`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        eans,
        from,
        to,
        grouped,
        hourly,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch meter values.");
    }
    console.log(response.json());
    return await response.json();
  } catch (error) {
    console.error("Error fetching meter values:", error);
    throw error;
  }
};

const PORTALS_BASE = import.meta.env.VITE_API_SERVICES || "http://localhost:8001";

const getPortalsToken = () => getSessionFromCookie()?.getAccessToken().getJwtToken();

export const fetchPortalsContracts = async (kboNumber: string): Promise<PortalsDocument[]> => {
  const response = await fetch(`${PORTALS_BASE}/api/v1/documents/contracts/${kboNumber}`, {
    headers: { Authorization: `Bearer ${getPortalsToken()}` },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const fetchPortalsStatements = async (kboNumber: string): Promise<PortalsDocument[]> => {
  const response = await fetch(`${PORTALS_BASE}/api/v1/documents/statements/${kboNumber}`, {
    headers: { Authorization: `Bearer ${getPortalsToken()}` },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const fetchPortalsDocumentUrl = async (
  kboNumber: string,
  documentKey: string,
  type?: string,
): Promise<string> => {
  const params = new URLSearchParams({ document_key: documentKey });
  if (type) params.set("type", type);
  const response = await fetch(`${PORTALS_BASE}/api/v1/documents/${kboNumber}/view?${params}`, {
    headers: { Authorization: `Bearer ${getPortalsToken()}` },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return data.url;
};

export {
  getPendingAgreements,
  getCompletedAgreements,
  getCompletedAgreement,
  getStatements,
  getStatement,
  getSignedMandates,
  getSignedMandate,
  getSwitchConfirmations,
  getEocConfirmations,
  getSwitchConfirmation,
};
